import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { ordersService } from '../services';
import type { CheckoutPayload } from '../types/api';
import type { Order, OrderContextType } from '../types';
import { useAuth } from './AuthContext';

const OrderContext = createContext<OrderContextType | null>(null);

export const ORDER_STATUS_LABELS: Record<Order['status'], string> = {
  pending: 'Sipariş Alındı',
  processing: 'Hazırlanıyor',
  shipped: 'Kargoda',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal Edildi',
};

export function OrderProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshOrders = useCallback(async (options?: { silent?: boolean }) => {
    if (!user) {
      setOrders([]);
      return;
    }

    if (!options?.silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const nextOrders = await ordersService.fetchOrders();
      setOrders(nextOrders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Siparişler yüklenemedi.');
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  }, [user]);

  const placeOrder = async (payload: CheckoutPayload) => {
    if (!user) {
      throw new Error('Sipariş vermek için giriş yapmalısınız.');
    }

    setError(null);
    const order = await ordersService.createOrder(payload);
    setOrders((prev) => [order, ...prev]);
    return order;
  };

  return (
    <OrderContext.Provider value={{ orders, placeOrder, refreshOrders, isLoading, error }}>
      {children}
    </OrderContext.Provider>
  );
}

export function useOrders() {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within OrderProvider');
  }
  return context;
}
